import {
  InlineFormattedNumber,
  Text,
  VStack,
  useHistoricalPrices,
  useObservableValue,
  type OHLCData,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { map, scan } from 'rxjs/operators';

interface OHLCVSummary {
  High: Big;
  Low: Big;
  Volume: Big;
}

interface SparklineSummaryProps {
  security: Security;
  exchangeMarkets: string[];
}

export function SparklineSummary({ security, exchangeMarkets }: SparklineSummaryProps) {
  // 1H resolution gives latest 24h prices history
  const sparklineDataObservable = useHistoricalPrices({
    symbol: security.Symbol,
    markets: exchangeMarkets,
    resolution: '1H',
  });

  const summary = useObservableValue(
    () =>
      sparklineDataObservable.pipe(
        scan((acc, data) => {
          const minDate = new Date();
          minDate.setHours(minDate.getHours() - 24);
          data.forEach(tick => {
            const ts = new Date(tick.Timestamp);
            if (ts < minDate) {
              acc.delete(tick.Timestamp);
            } else {
              acc.set(tick.Timestamp, tick);
            }
          });
          return acc;
        }, new Map<string, OHLCData>()),
        map(acc => {
          const result: Partial<OHLCVSummary> = {};
          acc.forEach(tick => {
            const currentHigh = Big(result?.High ?? 0);
            const tickHigh = Big(tick?.High ?? 0);
            const currentLow = Big(result?.Low ?? 0);
            const tickLow = Big(tick?.Low ?? 0);

            result.Volume = Big(tick.Volume).plus(result?.Volume ?? Big(0));
            result.High = tickHigh.gt(currentHigh) ? tickHigh : currentHigh;
            result.Low = currentLow.eq(0) || tickLow.lt(currentLow) ? tickLow : currentLow;
          });
          return result;
        })
      ),
    [sparklineDataObservable]
  );

  return (
    <>
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
          24h High
        </Text>
        <Text weight="fontWeightMedium" data-testid="24high">
          <InlineFormattedNumber
            specification={security.PriceDisplaySpec}
            number={summary?.High}
            increment={security.DefaultPriceIncrement}
          />
        </Text>
      </VStack>
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" transform="uppercase">
          24h Low
        </Text>
        <Text weight="fontWeightMedium" data-testid="24low">
          <InlineFormattedNumber
            specification={security.PriceDisplaySpec}
            number={summary?.Low}
            increment={security.DefaultPriceIncrement}
          />
        </Text>
      </VStack>
      <VStack alignItems="start" ml="spacing24">
        <Text size="fontSizeTiny" transform="uppercase">
          24h Volume
        </Text>
        <Text weight="fontWeightMedium" data-testid="24volume">
          <InlineFormattedNumber
            increment={security.DefaultPriceIncrement}
            number={summary?.Volume}
            currency={security.PositionCurrency}
          />
        </Text>
      </VStack>
    </>
  );
}
